import { SupportUserAvailabilityOverride } from "@redotech/cockroachdb-codegen/tables/support-v2/support-user-availability-override";
import ClockIcon from "@redotech/redo-icons/clock.svg";
import { useWorkScheduleQuery } from "@redotech/redo-merchant-app-common/setting/use-work-schedule-query";
import { nextTransitionAt } from "@redotech/redo-support-common/work-schedule/evaluate-work-schedule";
import {
  WEEKDAY_KEYS,
  type WeeklySchedule,
  type WorkSchedule,
} from "@redotech/redo-support-common/work-schedule/types";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo } from "react";

/** The wire types are deeply readonly; the evaluator wants mutable arrays. */
const toMutableHours = (
  hours: NonNullable<
    ReturnType<typeof useWorkScheduleQuery>["data"]
  >["schedule"]["weeklyHours"],
): WeeklySchedule => {
  const result: WeeklySchedule = {};
  for (const day of WEEKDAY_KEYS) {
    const dayHours = hours[day];
    if (dayHours) {
      result[day] = {
        enabled: dayHours.enabled,
        timeBlocks: dayHours.timeBlocks.map((block) => ({
          start: block.start,
          end: block.end,
        })),
      };
    }
  }
  return result;
};

const formatTime = (at: Date, timezone: string) =>
  new Intl.DateTimeFormat(undefined, {
    hour: "numeric",
    minute: "2-digit",
    timeZone: timezone,
  }).format(at);

const formatDay = (at: Date, timezone: string) =>
  new Intl.DateTimeFormat(undefined, {
    weekday: "long",
    month: "short",
    day: "numeric",
    timeZone: timezone,
  }).format(at);

export const ShiftStartingSoonModal = memo(function ShiftStartingSoonModal({
  startsAt,
  onClose,
}: {
  startsAt: Date;
  onClose: () => void;
}) {
  const scheduleQuery = useWorkScheduleQuery();
  const view = scheduleQuery.data?.schedule;

  const timezone = view?.timezone ?? "UTC";

  // The shift's end is simply the next flip after it starts.
  const endsAt = useMemo(() => {
    if (!view) {
      return null;
    }
    const schedule: WorkSchedule = {
      timezone: view.timezone,
      scheduleEnabled: view.scheduleEnabled,
      weeklyHours: toMutableHours(view.weeklyHours),
      scheduleOverrides: view.scheduleOverrides.map((entry) => ({
        id: entry.id,
        date: entry.date,
        available: entry.available,
        timeBlocks: entry.timeBlocks.map((block) => ({
          start: block.start,
          end: block.end,
        })),
        note: entry.note,
      })),
      assignableChannels: view.assignableChannels,
      overrideStatus:
        view.overrideStatus === "active"
          ? SupportUserAvailabilityOverride.Active
          : view.overrideStatus === "inactive"
            ? SupportUserAvailabilityOverride.Inactive
            : null,
      overrideExpiresAt: view.overrideExpiresAt,
    };
    try {
      return nextTransitionAt(schedule, new Date(startsAt.getTime() + 1000));
    } catch {
      return null;
    }
  }, [view, startsAt]);

  return (
    <RedoModal
      onModalCloseRequested={onClose}
      primaryButton={{ text: "Got it", onClick: onClose }}
      subtitle="You'll be marked available automatically when it begins."
      title="Your scheduled support shift is starting soon"
      TitleIcon={ClockIcon}
    >
      <Flex dir="column" gap="sm">
        <Flex justify="space-between">
          <Text fontSize="sm" textColor="tertiary">
            Starts
          </Text>
          <Text fontSize="sm" fontWeight="medium">
            {formatTime(startsAt, timezone)}
          </Text>
        </Flex>
        {endsAt && (
          <Flex justify="space-between">
            <Text fontSize="sm" textColor="tertiary">
              Ends
            </Text>
            <Text fontSize="sm" fontWeight="medium">
              {formatTime(endsAt, timezone)}
            </Text>
          </Flex>
        )}
        <Flex justify="space-between">
          <Text fontSize="sm" textColor="tertiary">
            Day
          </Text>
          <Text fontSize="sm" fontWeight="medium">
            {formatDay(startsAt, timezone)}
          </Text>
        </Flex>
        <Flex justify="space-between">
          <Text fontSize="sm" textColor="tertiary">
            Timezone
          </Text>
          <Text fontSize="sm" fontWeight="medium">
            {timezone}
          </Text>
        </Flex>
      </Flex>
    </RedoModal>
  );
});
